import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/leaderboard", label: "Stand" },
  { to: "/statistics", label: "Statistieken" },
  { to: "/tournaments", label: "Toernooien" },
];

export const Header = () => {
  const { isAdmin, logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="bg-white/80 backdrop-blur-xl border-b border-[#E5E5E5] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group" onClick={closeMenu}>
          <img src="/anni-logo-square.png" alt="Anni's Place" className="w-10 h-10 object-contain" />
          <span className="font-heading font-extrabold text-base sm:text-lg tracking-tight leading-tight">
            Anni's Place <span className="text-[#A8882D]">Darts</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  active ? "text-[#0A0A0A]" : "text-[#525252] hover:text-[#0A0A0A]"
                }`}
              >
                {item.label}
                {active && <div className="h-0.5 bg-[#D4AF37] mt-1.5" />}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {user && user !== false && isAdmin ? (
            <>
              <Link
                to="/admin"
                data-testid="admin-link"
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#A8882D]"
              >
                <ShieldCheck className="w-4 h-4" /> Beheer
              </Link>
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="logout-btn"
                className="hidden sm:flex items-center gap-1.5 bg-[#0A0A0A] text-[#D4AF37] px-4 py-2 text-sm font-semibold hover:bg-[#1A1A1A] transition-colors"
              >
                <LogOut className="w-4 h-4" /> Uitloggen
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="login-link"
              className="hidden sm:flex bg-[#0A0A0A] text-[#D4AF37] px-5 py-2 text-sm font-semibold hover:bg-[#1A1A1A] transition-colors"
            >
              Beheer Login
            </Link>
          )}

          {/* Mobiele hamburger-knop */}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={menuOpen}
            className="md:hidden flex items-center justify-center w-10 h-10 text-[#0A0A0A] hover:text-[#A8882D] transition-colors"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobiel uitklapmenu */}
      {menuOpen && (
        <nav className="md:hidden border-t border-[#E5E5E5] bg-white" data-testid="mobile-menu">
          <div className="px-4 py-2 flex flex-col">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={`px-2 py-3 text-base font-medium border-b border-[#F2F0EA] last:border-0 flex items-center justify-between ${
                    active ? "text-[#A8882D]" : "text-[#0A0A0A]"
                  }`}
                >
                  {item.label}
                  {active && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />}
                </Link>
              );
            })}

            {user && user !== false && isAdmin ? (
              <>
                <Link
                  to="/admin"
                  onClick={closeMenu}
                  data-testid="mobile-admin-link"
                  className="px-2 py-3 text-base font-semibold text-[#0A0A0A] flex items-center gap-2 border-t border-[#F2F0EA]"
                >
                  <ShieldCheck className="w-4 h-4" /> Beheer
                </Link>
                <button
                  onClick={() => {
                    logout();
                    closeMenu();
                    navigate("/");
                  }}
                  data-testid="mobile-logout-btn"
                  className="mt-2 mb-3 inline-flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-[#D4AF37] px-4 py-3 text-sm font-semibold"
                >
                  <LogOut className="w-4 h-4" /> Uitloggen
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={closeMenu}
                data-testid="mobile-login-link"
                className="mt-2 mb-3 inline-flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37] px-5 py-3 text-sm font-semibold"
              >
                Beheer Login
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};
